// frontend/src/admin/v2/components/IntegrationMonogram.jsx
import { Box } from '@mui/material'
import { V2 } from '../../../theme/tokens'

/**
 * IntegrationMonogram — 32×32 brand-tinted chip with a 1-2 letter glyph.
 *
 * @description
 *   Used as the visual mark for every integration card. Shipping real
 *   brand SVGs is licensing-risky and uneven across services we connect
 *   to (some German B2B services have no public mark), so V1 uses a
 *   tinted monogram everywhere for a consistent visual rhythm.
 *
 *   Tints derive from each brand's primary color where known. Unknown
 *   tints fall back to a neutral grey.
 *
 * @useWhen
 *   - Inside IntegrationCard's logo slot
 *   - Anywhere a 32×32 admin-area integration mark is needed
 *
 * @props
 *   letter: string — 1 or 2 characters (max). Uppercased on render.
 *   tint:   string — palette key from TINTS below
 *   size:   number — px (default 32). Foreground letter scales accordingly.
 */

// Brand-tinted palette. Keys mirror registry.yaml's monogram_tint field.
//
// 🔴 DIE FLAECHE IST EIN SOFT-TOKEN, DIE SCHRIFT DER `*Text`-TON.
// Die `*Text`-Werte (`okText`, `infoText`, …) sind auf Text AUF heller Flaeche
// abgestimmt — `tokens.js:526-529` sagt das woertlich ("text token tuned one
// step darker … so it stays >=4.7:1 on its own -soft fill"). Sie als FUELUNG
// zu nehmen ist ein Kategorienfehler: die Gates zu PR 4 haben am 2026-10-04
// zwei Monogramme dabei erwischt — `supabase` (`okText` als Grund UND `ink`
// als Schrift) fiel auf 3,56:1, `default` (`ink45` als Grund) auf 2,93:1.
//
// Kontrast je Zeile nachgerechnet (WCAG-Relativluminanz, Buchstabe ist
// `Math.round(size*0.45)` = 14 px fett -> AA-Schwelle 4,5:1, KEIN Gross-Text):
// Exportiert, damit `IntegrationMonogram.test.jsx` jeden Eintrag nachrechnen
// kann, statt eine zweite Farbtabelle zu pflegen.
export const TINTS = {
  shopify:   { bg: V2.okSoft,   fg: V2.okText },    // 4,75:1  Shopify-Gruen
  openai:    { bg: V2.ink,      fg: '#10A37F' },    // 5,58:1  OpenAI-Marke
  anthropic: { bg: '#D4A27F',   fg: V2.ink },       // 7,88:1  Anthropic-Ocker
  // Marken-Teal, eine Stufe dunkler als das fruehere '#1F8B8B' (4,10:1, schon
  // vor diesem PR unter AA). Tonlage unveraendert (180 Grad), nur der Wert.
  perplexity:{ bg: '#1A7A7A',   fg: '#FFFFFF' },    // 5,11:1  Perplexity-Teal
  supabase:  { bg: V2.okSoft,   fg: V2.okText },    // 4,75:1  Supabase-Gruen
  lexware:   { bg: V2.infoSoft, fg: V2.infoText },  // 4,75:1  Lexware-Blau
  lsm:       { bg: V2.warnSoft, fg: V2.warnText },  // 4,84:1  LSM-Orange
  smtp:      { bg: V2.wash,     fg: V2.ink },       // 16,3:1  Neutral
  rma:       { bg: V2.errSoft,  fg: V2.errText },   // 5,91:1  Returns-Rot
  inquiry:   { bg: V2.infoSoft, fg: V2.infoText },  // 4,75:1  Inquiry-Blau
  default:   { bg: V2.wash,     fg: V2.ink },       // 16,3:1  Neutraler Rueckfall
}

export default function IntegrationMonogram({ letter = '?', tint = 'default', size = 32 }) {
  const { bg, fg } = TINTS[tint] || TINTS.default
  const letters = String(letter).slice(0, 2).toUpperCase()
  return (
    <Box
      role="img"
      aria-label={`${letters} integration mark`}
      sx={{
        width: size,
        height: size,
        borderRadius: '8px',
        bgcolor: bg,
        color: fg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: Math.round(size * 0.45),
        fontFamily: 'Helvetica, Arial, sans-serif',
        letterSpacing: '-0.02em',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {letters}
    </Box>
  )
}
